import React, { useEffect, useState } from "react";
import { Button, Grid2, GridDirection, IconButton, useMediaQuery, useTheme } from "@mui/material";
import { FilterAlt } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { useDispatch, useSelector } from "../../../../app/helpers";
import { RootState } from "../../../../app/rootReducer";
import { FilterSlideoutDialog } from "../FilterSlideout/FilterSlideoutDialog.component";
import {
  SelectedZoneOrWorkplaceType,
  setFilterSelected,
  setIsSelected,
  setLoading,
  setSelectedBookingType,
  setSelectedTimeframe,
  setSelectedZoneOrWorkplace,
  setSelectedByAttFilter
} from "../../../../features/Reports/slices/report.slice";
import { fetchReport } from "../../../../features/Reports/thunks/report.thunk";
import { CapacityObject, FloorPlanMemo } from "../../../../features/Reports/typings/reports.types";
import { BookingType } from "../../../../features/Booking-Form/typings/booking-inputs";
import FloorSingleSelector from "../../FacilityReport/floor-single-selector.component";
import { StartDatePicker } from "../../DateSelector/DatePickers/StartDatePicker";
import { EndDatePicker } from "../../DateSelector/DatePickers/EndDatePicker";
import BookingTypeSelector from "./booking-type-selector.partial";
import DefaultSpinner from "../../../LoadingSpinner/default-spinner.component";
import SelectionChips from "../../FacilityReport/selection-chips.component";
import { refetchCapaReportWithSelected } from "../../functions/refetchCapaReportWithSelected.funtion";
import { initialWeekdays } from "../../../../features/Reports/initialWeekdays.constant";
import { useFilterSelectorStyles } from "./hooks/useFilterSelectorStyles";
import { areDaysEqual } from "./functions/areWeekdaysEqual.function";
import { buildCapacityReportAvailableTypes } from "./capacity-report-available-types";

export type FilterTypState = "floor" | "location" | SelectedZoneOrWorkplaceType | undefined;

type P = {
  isGroupFilterUse: boolean;
  zoneBookingReport: boolean;
  entityData: (CapacityObject | undefined)[] | undefined;
  isReportFetched: boolean | undefined;
  floorData?: FloorPlanMemo;
};

export const CapacityFilterAndSelector: React.FC<P> = ({
  isGroupFilterUse,
  zoneBookingReport,
  entityData,
  isReportFetched,
  floorData
}) => {
  const {
    currentFloors,
    initialTimeframe,
    zoneRestrictions,
    isLoading,
    calculateWithoutCosts,
    selectedTimeframe,
    selectedZoneOrWorkplace,
    selectedBookingType,
    filterSelected: { selectedWeekdays, selectedAttributes, selectedDepartmentIds }
  } = useSelector((state: RootState) => state.report);
  const {
    userInformation: { sub, placeTypes, zoneTypes, company },
    userRoles: { isHrAdmin }
  } = useSelector((state: RootState) => state.login);
  const workplaceBookingEnabled = company?.meta?.workplaceBookingEnabled;
  const dispatch = useDispatch();

  const { t } = useTranslation();
  const theme = useTheme();
  const isBrowser = useMediaQuery(theme.breakpoints.up(768));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    selectorContainerStyle,
    datePickerContainerStyle,
    datePickerChildStyle,
    typeAndFilterContainerStyle,
    groupFilterBtnStyle
  } = useFilterSelectorStyles(isMobile, isBrowser, theme);

  const [fetchEnabled, setFetchEnabled] = useState(false);
  const [filterTyp, setFilterTyp] = useState<FilterTypState[]>(["floor", undefined]);

  // group filter slide open
  const [filterSlideOpen, setFilterSlideOpen] = useState(false);

  // temporarily store the booking type to compare redux state so that display calculate button or not
  const [tempBookingType, setTempBookingType] = useState<BookingType | null>(null);

  // used for only internal time change with date selector
  const [changeTime, setChangeTime] = useState({
    timeframeStart: selectedTimeframe?.timeframeStart || DateTime.local().toISO(),
    timeframeEnd: selectedTimeframe?.timeframeEnd || DateTime.local().toISO()
  });

  const basicParams = {
    userId: sub,
    calculateWithoutCosts: true,
    calcPerDay: true,
    calcPerWeekday: true,
    calcPerWorkplace: false, // filterTyp !== "location",
    calcPerHour: true,
    reportType: "capacityManager",
    start: selectedTimeframe?.timeframeStart as string,
    end: selectedTimeframe?.timeframeEnd as string,
    companyId: zoneRestrictions?.company?.id,
    weekdays: selectedWeekdays,
    status: true,
    update: false,
    sub: sub,
    cost: calculateWithoutCosts,
    zoneBooking: zoneBookingReport
  };

  const defaultParams = {
    cost: calculateWithoutCosts,
    sub: sub,
    start: selectedTimeframe?.timeframeStart as string,
    end: selectedTimeframe?.timeframeEnd as string,
    filterType: filterTyp,
    zoneBooking: zoneBookingReport,
    companyId: zoneRestrictions?.company?.id,
    zoneBookingObject: zoneRestrictions?.object?.zoneBookingObject,
    status: true,
    update: false,
    weekdays: selectedWeekdays
  };

  const availableTypes = buildCapacityReportAvailableTypes(
    placeTypes,
    zoneTypes,
    workplaceBookingEnabled
  );

  const isGroupFilterApplied =
    !areDaysEqual(selectedWeekdays, initialWeekdays) ||
    (selectedAttributes && selectedAttributes.length > 0) ||
    (selectedDepartmentIds && selectedDepartmentIds.length > 0);

  const resetDispatchGroupFilter = () => {
    dispatch(
      setFilterSelected({
        // selectedWeekdays, we don't reset to initial weekdays
        selectedDepartmentIds: [],
        selectedAttributes: []
      })
    );
  };

  useEffect(() => {
    // have workplace booking type always preselected
    // if workplace does not exist preselect the first existing booking type in the list
    dispatch(setSelectedBookingType(availableTypes[0].name as BookingType));
    setTempBookingType(availableTypes[0].name as BookingType);
    resetDispatchGroupFilter();
  }, []);

  useEffect(() => {
    // when the place or zone is selected on the floor plan
    refetchCapaReportWithSelected({
      defaultParams,
      currentFloors,
      fetchReport,
      selectedZoneOrWorkplace,
      selectedWeekdays,
      setFilterTyp,
      dispatch
    });
  }, [selectedZoneOrWorkplace, selectedTimeframe]);

  return (
    <Grid2 container alignItems={"center"}>
      {selectedTimeframe && (
        <Grid2
          container
          data-testid="selector-container"
          alignItems={selectorContainerStyle.alignItems}
          direction={selectorContainerStyle.direction as GridDirection}
          margin={selectorContainerStyle.margin}
          sx={{
            pt: selectorContainerStyle.pt,
            pl: selectorContainerStyle.pl,
            flexWrap: selectorContainerStyle.flexWrap
          }}
        >
          {/* floor single selector before report filters */}
          <FloorSingleSelector zoneBooking={zoneBookingReport} />

          {/* date picker */}
          <Grid2
            container
            data-testid="selector-datePicker-container"
            sx={{
              width: datePickerContainerStyle.width,
              minWidth: datePickerContainerStyle.minWidth
            }}
          >
            {/* start date picker */}
            <Grid2 sx={{ width: datePickerChildStyle.width }}>
              <StartDatePicker
                selectedTimeframe={selectedTimeframe}
                initialTimeframe={initialTimeframe}
                onEnableFetch={() => setFetchEnabled(true)}
                changeTime={changeTime}
                setChangeTime={({ start, end }: { start: string; end: string }) => {
                  setChangeTime({ timeframeStart: start, timeframeEnd: end });
                  dispatch(setSelectedTimeframe({ timeframeStart: start, timeframeEnd: end }));
                }}
              />
            </Grid2>

            {/* end date picker */}
            <Grid2 sx={{ width: datePickerChildStyle.width }}>
              <EndDatePicker
                selectedTimeframe={selectedTimeframe}
                initialTimeframe={initialTimeframe}
                onEnableFetch={() => setFetchEnabled(true)}
                changeTime={changeTime}
                setChangeTime={({ start, end }: { start: string; end: string }) => {
                  setChangeTime({ timeframeStart: start, timeframeEnd: end });
                  dispatch(setSelectedTimeframe({ timeframeStart: start, timeframeEnd: end }));
                }}
              />
            </Grid2>
          </Grid2>

          <Grid2
            container
            data-testid="selector-typeAndFilter-container"
            sx={{
              rowGap: typeAndFilterContainerStyle.rowGap,
              alignItems: "center",
              width: "fit-content"
            }}
          >
            {isReportFetched ? (
              <>
                {/* booking type picker */}
                <Grid2>
                  <BookingTypeSelector
                    typToggle={selectedBookingType ?? null}
                    setTypToggle={typToggle => {
                      dispatch(setSelectedBookingType(typToggle));

                      // if booking type toggle is differed from already selected ZoneOrWorkplace's booking type
                      // then remove the selection
                      /* istanbul ignore next */
                      if (
                        selectedZoneOrWorkplace &&
                        selectedZoneOrWorkplace.bookingType !== typToggle
                      ) {
                        dispatch(setIsSelected(false));
                        dispatch(
                          setSelectedZoneOrWorkplace({
                            id: [],
                            name: [],
                            type: SelectedZoneOrWorkplaceType.ZONE,
                            open: false,
                            floorInventoryId: 0,
                            bookingType: undefined
                          })
                        );
                        dispatch(
                          setSelectedByAttFilter({
                            id: [],
                            name: [],
                            type: SelectedZoneOrWorkplaceType.ZONE,
                            open: false,
                            floorInventoryId: -1,
                            bookingType: undefined
                          })
                        );
                        resetDispatchGroupFilter();
                      }
                    }}
                    onEnableFetch={newToggles => /* istanbul ignore next */ {
                      if (tempBookingType === newToggles) setFetchEnabled(false);
                      else setFetchEnabled(true);
                    }}
                  />
                </Grid2>

                {isGroupFilterUse && (
                  <Grid2 sx={{ mr: 2 }}>
                    <IconButton
                      color={isGroupFilterApplied ? "primary" : "inherit"}
                      sx={{
                        ml: groupFilterBtnStyle.ml,
                        minWidth: groupFilterBtnStyle.minWidth,
                        height: groupFilterBtnStyle.height,
                        border: groupFilterBtnStyle.border,
                        borderRadius: 1
                      }}
                      onClick={() => setFilterSlideOpen(true)}
                      data-testid="filter-slide-open-btn"
                    >
                      <FilterAlt />
                    </IconButton>
                  </Grid2>
                )}
              </>
            ) : (
              <Button
                loading
                variant={"contained"}
                sx={{ height: groupFilterBtnStyle.loadingBtnHeight }}
              />
            )}

            {/* calculate button */}
            {fetchEnabled && (
              <Grid2>
                <Button
                  data-testid="calculate-btn"
                  onClick={() => {
                    const selectedType =
                      selectedBookingType || (availableTypes[0].name as BookingType);
                    setTempBookingType(selectedType);

                    dispatch(setLoading(true));

                    currentFloors.forEach(
                      floor =>
                        dispatch(
                          fetchReport({
                            ...basicParams,
                            id: floor.id,
                            filterType: "floor",
                            start: selectedTimeframe.timeframeStart,
                            end: selectedTimeframe.timeframeEnd,
                            bookingType: selectedType
                          })
                        ) // .then(() => setFetchEnabled(false))
                    );
                    setFetchEnabled(false);
                  }}
                >
                  {t("Calculate")}
                  {isLoading ? (
                    <DefaultSpinner
                      style={{ padding: 0, paddingLeft: "10px" }}
                      color="white"
                      size={20}
                    />
                  ) : null}
                </Button>
              </Grid2>
            )}
          </Grid2>

          {/* chips when selecting place or zone */}
          <Grid2>
            <SelectionChips
              refetchOnRemove={() => {
                resetDispatchGroupFilter();
                dispatch(setSelectedBookingType(availableTypes[0].name as BookingType));
                setTimeout(
                  () =>
                    currentFloors.forEach(floor =>
                      dispatch(fetchReport({ ...defaultParams, id: floor.id, filterType: "floor" }))
                    ),
                  5
                );
              }}
            />
          </Grid2>
        </Grid2>
      )}

      {/* capacity group filter dialog - weekdays, department, attributes */}
      <FilterSlideoutDialog
        isOpen={filterSlideOpen}
        setIsOpen={setFilterSlideOpen}
        setFetchEnabled={setFetchEnabled}
        isHrAdmin={isHrAdmin}
        floorData={floorData}
        filterType={filterTyp}
        basicParams={basicParams}
        entityData={entityData as (CapacityObject | undefined)[]} // when isReportFetched(include entityData) is true, this dialog can be open
      />
    </Grid2>
  );
};
